import {
  ErrorView,
  MarkdownViewer,
  PageLayout,
  SkeletonStack,
} from "@gearbox-protocol/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

const legalDocuments: Record<string, { file: string; title: string }> = {
  "privacy-notice": {
    file: "privacy-notice.md",
    title: "Privacy Notice",
  },
  "terms-of-service": {
    file: "term-of-service.md",
    title: "Terms of Service",
  },
  "risk-disclosure": {
    file: "risk-disclosure-statement.md",
    title: "Risk Disclosure Statement",
  },
};

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>();

  const docInfo = slug ? legalDocuments[slug] : undefined;

  const {
    data: content,
    error,
    isLoading,
  } = useQuery<string, Error>({
    queryKey: ["legal-document", slug],
    queryFn: async () => {
      if (!docInfo) {
        throw new Error("Document not found");
      }
      const response = await fetch(`/legal/${docInfo.file}`);
      if (!response.ok) {
        throw new Error("Failed to load document");
      }
      return response.text();
    },
    enabled: !!docInfo,
  });

  if (!docInfo) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return (
      <PageLayout title={docInfo.title}>
        <SkeletonStack lines={20} />
      </PageLayout>
    );
  }

  if (error || !content) {
    return (
      <ErrorView
        status={404}
        message="Document not found"
        backButton={{
          text: "Back to Home",
          href: "/",
        }}
      />
    );
  }

  return <MarkdownViewer content={content} title={docInfo.title} />;
}

function NotFoundPage() {
  return (
    <div className="w-full flex items-center justify-center py-16">
      <div className="flex flex-col gap-8 items-center max-w-md mx-auto px-4 py-8 text-center">
        <div className="flex flex-col items-center">
          <h1 className="text-6xl font-bold text-foreground font-mono">404</h1>
          <h2 className="text-2xl font-semibold text-foreground">
            Document Not Found
          </h2>
        </div>

        <div className="flex flex-col items-center gap-2">
          <p>
            The legal document you&apos;re looking for doesn&apos;t exist or has
            been moved.
          </p>
        </div>
      </div>
    </div>
  );
}
